import React, {
  useRef,
  useState,
  useCallback,
  useEffect,
} from 'react'
import { Map, List } from 'immutable'
import { connect as connectRedux } from 'react-redux'
import { useMutation } from '@apollo/client'
import { getBoundActions } from '../../../actions/index'
import {
  getFormattedContentData,
  createTitleGroupId,
  CONTENT_IMAGE_TYPE_KEYART_WITH_TEXT,
} from '../../../services/content/index'
import { formatDuration } from '../../../theme/web-app/index'
import { TYPE_CONTENT_COURSE } from '../../../services/content-type/index'
import { getAuthIsLoggedIn } from '../../../services/auth/index'
import {
  USER_STATE_PROGRESS_ITEM_TYPE_VIDEO,
  COURSE_STATE_COMPLETE,
  COURSE_STATE_IN_PROGRESS,
} from '../../../services/detail/index'
import { PLAY_CLICK_GA4_EVENT } from '../../../services/event-tracking/index'
import { USER_STATE_CREATE_OR_UPDATE } from '../../../services/detail/query'
import Button, { TYPES } from '../../Button/index'
import CheckIcon from '../../Icon/icons/Check/index'
import ReplayIcon from '../../Icon/icons/Replay/index'
import Picture, { IMAGE_TYPE_WEBP, IMAGE_LOADING_LAZY } from '../../Picture/index'
import ProgressBar from '../../ProgressBar/index'
import Link from '../../Link/index'
import EllipsisVerticalIcon from '../../Icon/icons/EllipsisVertical/index'
import DetailPageV3ActionPlaylist from '../DetailPageV3ActionButtons/DetailPageV3ActionPlaylist'
import { getOptimizedImageUrl, checkIfCourseWithoutProgress } from '../utils'

/**
 * @param {Object} props - The props
 * @param {Map} props.auth - The auth data
 * @param {Map} props.itemData - The course module item data with label
 * @param {number} props.index - The index of the item
 * @param {number} props.courseContentId - The course content ID
 * @param {function} props.onCourseStateChange - The function to handle course state change
 * @returns {JSX.Element} DetailPageV3CourseModuleItem
 */

const DetailPageV3CourseModuleItem = (props) => {
  const {
    auth,
    itemData = Map(),
    courseContentId,
    onCourseStateChange,
    setEventDefault,
  } = props

  const dropdownRef = useRef(null)
  const jwt = auth.get('jwt')
  const isLoggedIn = getAuthIsLoggedIn(auth)
  const tileData = getFormattedContentData(itemData.get('item'))
  const label = itemData.get('label')
  const itemId = tileData.get('id')
  const itemTitleGroupId = tileData.get('titleGroupId')
  const isAllowed = tileData.getIn(['featureMedia', 'contentPolicy', 'accessCheck', 'allowed'])
  const duration = tileData.getIn(['featureMedia', 'duration'], 0) || 0
  const userMediaPosition = tileData.getIn(['featureMedia', 'userMediaPosition', 'position'], 0) || 0
  const hasProgressBar = userMediaPosition > 0
  const completed = itemData.getIn(['item', 'courseItemProgress', 'state']) === COURSE_STATE_COMPLETE
  const image = tileData.get('images', List()).find((i) => i.get('type') === CONTENT_IMAGE_TYPE_KEYART_WITH_TEXT) || Map()
  const { fallbackImageUrl, imageUrl } = getOptimizedImageUrl(image.get('url'), '320')

  const ga4EventData = {
    content_name: tileData.get('title'),
    content_id: tileData.get('contentId'),
    content_type: tileData.get('contentType'),
    media_id: tileData.getIn(['featureMedia', 'mediaId']),
    media_language: tileData.get('languageFullText'),
    parent_series_id: tileData.get('seriesId'),
    parent_series_name: tileData.get('seriesTitle'),
    is_featured_media: true,
    ui_location: 'Course page',
    ui_element: 'Tile',
  }

  const isCourseWithoutProgress = checkIfCourseWithoutProgress(courseContentId)
  const showProgressButton = !!itemId
    && isLoggedIn
    && !isCourseWithoutProgress

  const [isExpanded, setIsExpanded] = useState(false)

  const [userStateCreateOrUpdate, { loading }] = useMutation(USER_STATE_CREATE_OR_UPDATE, {
    variables: {
      progressItemType: USER_STATE_PROGRESS_ITEM_TYPE_VIDEO,
      progressItemId: itemTitleGroupId,
      parentContentTitleGroupId: createTitleGroupId(courseContentId, TYPE_CONTENT_COURSE),
      state: completed ? COURSE_STATE_IN_PROGRESS : COURSE_STATE_COMPLETE,
    },
    context: {
      jwt,
    },
  })

  const handleStateChange = async () => {
    await userStateCreateOrUpdate()
    onCourseStateChange()
  }
  const handleLinkClick = () => {
    // GA4 events
    setEventDefault(PLAY_CLICK_GA4_EVENT.mergeDeep(ga4EventData))
  }

  const className = [
    'detail-page-v3__course-module-item',
    completed ? 'detail-page-v3__course-module-item--completed' : '',
  ].filter((cls) => !!cls).join(' ')

  const buttonClassName = [
    'detail-page-v3__course-module-item-button',
    completed ? 'detail-page-v3__course-module-item-button--completed' : '',
  ].filter((cls) => !!cls).join(' ')
  const dropdownClassName = [
    'detail-page-v3__course-module-item-actions-dropdown',
    isExpanded ? 'detail-page-v3__course-module-item-actions-dropdown--expanded' : null,
  ].filter((cls) => !!cls).join(' ')

  const handleClickOutside = useCallback((event) => {
    if (dropdownRef && dropdownRef.current && !dropdownRef.current.contains(event.target)) {
      setIsExpanded(false)
    }
  }, [dropdownRef])

  useEffect(() => {
    document.addEventListener('click', handleClickOutside)
    return () => {
      document.removeEventListener('click', handleClickOutside)
    }
  }, [handleClickOutside])

  return (
    <div className={className} key={itemId}>
      <div className="detail-page-v3__course-module-item-header">
        <h5 className="detail-page-v3__course-module-item-title">
          {label}
        </h5>
        {showProgressButton && (
          <Button
            className={buttonClassName}
            type={TYPES.ICON}
            iconSvg={completed ? <ReplayIcon /> : <CheckIcon />}
            onClick={handleStateChange}
            disabled={loading}
          />
        )}
      </div>
      <div className="detail-page-v3__course-module-item-content">
        <div className="detail-page-v3__episode-item-container">
          <div className="detail-page-v3__episode-item-image">
            <Link
              to={tileData.get('path')}
              query={isAllowed ? { fullplayer: 'feature' } : undefined}
              onClick={handleLinkClick}
            >
              <Picture
                fallbackSrc={fallbackImageUrl}
                alt={tileData.get('title')}
                loading={IMAGE_LOADING_LAZY}
                sources={[
                  {
                    type: IMAGE_TYPE_WEBP,
                    srcSet: imageUrl,
                  },
                ]}
              />
              {hasProgressBar && (
                <div className="detail-page-v3__episode-item-progress-bar">
                  <ProgressBar
                    duration={duration}
                    userInfo={Map({ featurePosition: userMediaPosition })}
                  />
                </div>
              )}
              {completed && !isCourseWithoutProgress && <div className="detail-page-v3__course-module-item-overlay">
                <span className="detail-page-v3__course-module-item-overlay-icon--mobile">
                  <CheckIcon size={52} />
                </span>
                <span className="detail-page-v3__course-module-item-overlay-icon--desktop">
                  <CheckIcon size={96} />
                </span>
              </div>}
            </Link>
          </div>
          <div className="detail-page-v3__episode-item-details">
            {!!label && (
              <p className="detail-page-v3__course-module-item-label">
                {label}
              </p>
            )}
            <Link
              to={tileData.get('path')}
              query={isAllowed ? { fullplayer: 'feature' } : undefined}
              onClick={handleLinkClick}
            >
              <h5 className="detail-page-v3__episode-item-title">
                {tileData.get('title')}
              </h5>
            </Link>
            <p className="detail-page-v3__episode-item-duration">
              {formatDuration(duration)}
            </p>
          </div>
          {isLoggedIn && (
            <div className="detail-page-v3__course-module-item-actions">
              <div ref={dropdownRef}>
                <Button
                  type={TYPES.ICON}
                  iconSvg={<EllipsisVerticalIcon />}
                  onClick={() => setIsExpanded(!isExpanded)}
                  className="detail-page-v3__course-module-item-button"
                />
              </div>
              <div className={dropdownClassName}>
                <DetailPageV3ActionPlaylist
                  detailData={tileData}
                  ga4EventData={ga4EventData}
                />
                {showProgressButton && (
                  <Button
                    className="detail-page-v3__course-module-item-button"
                    type={TYPES.ICON}
                    iconSvg={completed ? <ReplayIcon /> : <CheckIcon />}
                    onClick={handleStateChange}
                  />
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default connectRedux((state) => ({
  auth: state.auth,
}), (dispatch) => {
  const actions = getBoundActions(dispatch)
  return {
    setEventDefault: actions.eventTracking.setEventDefault,
  }
})(DetailPageV3CourseModuleItem)
